import { BETA_ENGAGEMENT, recordBetaEngagement } from './betaToolbarEligibility';

const RECENTLY_VIEWED_KEY = 'shopsmt_recently_viewed';
const MAX_RECENTLY_VIEWED = 24;

/** @type {Set<(products: object[]) => void>} */
const subscribers = new Set();

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '') {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage unavailable */
  }
}

function productKey(product) {
  const url = typeof product?.url === 'string' ? product.url.trim() : '';
  if (url !== '') {
    return url;
  }

  const id = product?.id;
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }

  return '';
}

/**
 * @param {object} product
 * @returns {object|null}
 */
function snapshotProduct(product) {
  if (product == null || typeof product !== 'object') {
    return null;
  }

  const key = productKey(product);
  if (key === '') {
    return null;
  }

  const title = typeof product.title === 'string' ? product.title.trim() : '';
  if (title === '') {
    return null;
  }

  return {
    id: product.id ?? null,
    title,
    url: typeof product.url === 'string' ? product.url.trim() : '',
    image: typeof product.image === 'string' ? product.image.trim() : '',
    priceNow: product.priceNow ?? null,
    priceWas: product.priceWas ?? null,
    merchant: typeof product.merchant === 'string' ? product.merchant.trim() : '',
    inStock: product.inStock ?? null,
    viewedAt: Date.now(),
  };
}

/**
 * @returns {object[]}
 */
export function getRecentlyViewedProducts() {
  const data = readJson(RECENTLY_VIEWED_KEY);
  if (!Array.isArray(data)) {
    return [];
  }

  return data.filter((entry) => snapshotProduct(entry) != null);
}

function writeRecentlyViewedProducts(products) {
  writeJson(RECENTLY_VIEWED_KEY, products);
  for (const callback of subscribers) {
    callback(products);
  }
}

/**
 * @param {(products: object[]) => void} callback
 * @returns {() => void}
 */
export function subscribeRecentlyViewedProducts(callback) {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * @param {object} product
 * @returns {boolean}
 */
export function isRecentlyViewedProduct(product) {
  const key = productKey(product);
  if (key === '') {
    return false;
  }

  return getRecentlyViewedProducts().some(
    (entry) => productKey(entry) === key,
  );
}

/**
 * @param {object} product
 */
export function removeRecentlyViewedProduct(product) {
  const key = productKey(product);
  if (key === '') {
    return;
  }

  const next = getRecentlyViewedProducts().filter(
    (entry) => productKey(entry) !== key,
  );
  writeRecentlyViewedProducts(next);
}

/**
 * @param {object} product
 */
export function addRecentlyViewedProduct(product) {
  const snapshot = snapshotProduct(product);
  if (snapshot == null) {
    return;
  }

  const key = productKey(snapshot);
  const existing = getRecentlyViewedProducts().filter(
    (entry) => productKey(entry) !== key,
  );

  writeRecentlyViewedProducts([snapshot, ...existing].slice(0, MAX_RECENTLY_VIEWED));
  recordBetaEngagement(BETA_ENGAGEMENT.PRODUCT_VIEW);
}
